import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import AuthUser from '../../../Components/AuthUser';
import { useReactToPrint } from 'react-to-print';
import http from '../../../http';
import { num } from '../../../converters';
import ReactDatePicker from 'react-datepicker';

export default function UserWiseReport() {
    const { user } = AuthUser();
    const date = moment().format('YYYY-MM-DD');
    const [filterData, setFilterData] = useState({ startDate: date, endDate: date });
    const [reportData, setReportData] = useState([]);
    const [userList, setUserList] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');

    // Load user list once on mount
    useEffect(() => {
        http.get('user-list').then(res => {
            setUserList(res?.data || []);
        });
        fetchReport(date, date);
    }, []);

    const fetchReport = (startDate, endDate, userId = '') => {
        const payload = { startDate, endDate };
        if (userId) payload.user_id = userId;
        http.post('user-wise-report', payload)
            .then(res => {
                setReportData(res?.data?.data || []);
            });
    };

    const handleFilter = () => {
        fetchReport(filterData.startDate, filterData.endDate, selectedUserId);
    };

    const reportRef = useRef();
    const handlePrint = useReactToPrint({ content: () => reportRef.current });

    // Selected user label for print header
    const selectedUserLabel = selectedUserId
        ? userList.find(u => String(u.saas_user_id) === String(selectedUserId))?.name || ''
        : '';
    const grandTotalSales = reportData.reduce((s, u) => s + (u.invoices || []).reduce((a, inv) => a + parseFloat(inv.grand_total || 0), 0), 0);
    const grandTotalPaid = reportData.reduce((s, u) => s + (u.invoices || []).reduce((a, inv) => a + parseFloat(inv.paid_amount || 0), 0), 0);
    const grandTotalReturn = reportData.reduce((s, u) => s + (u.invoices || []).reduce((a, inv) => a + parseFloat(inv.return_amount || 0), 0), 0);
    const grandTotalDiscount = reportData.reduce((s, u) => s + (u.invoices || []).reduce((a, inv) => a + parseFloat(inv.special_discount || 0), 0), 0);
    const grandTotalInvoices = reportData.reduce((s, u) => s + (u.invoices || []).length, 0);
    const grandNetAmount = grandTotalPaid - grandTotalReturn;
    const grandNetTotal = grandTotalSales - grandTotalDiscount;


    return (
        <div className="page-content">
            {/* Filter Bar */}
            <div className="row ms-1">
                <div className="col-lg-12 requisition_status_blog">
                    <div className="card mb-2 billing_status">
                        <div className="row mb-1 p-2 align-items-end">
                            <div className="col-sm-3">
                                <div className="row align-items-center">
                                    <div className="col-4"><label className="mb-0">Start Date</label></div>
                                    <div className="col-8">
                                        <ReactDatePicker
                                            placeholderText="From Date"
                                            selected={filterData.startDate ? new Date(filterData.startDate) : new Date()}
                                            className="form-control form-control-sm custom-datepicker-input-width"
                                            dateFormat="dd/MM/yyyy"
                                            onChange={(d) => setFilterData({ ...filterData, startDate: moment(d).format('YYYY-MM-DD') })}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-sm-3">
                                <div className="row align-items-center">
                                    <div className="col-4"><label className="mb-0">End Date</label></div>
                                    <div className="col-8">
                                        <ReactDatePicker
                                            placeholderText="To Date"
                                            selected={filterData.endDate ? new Date(filterData.endDate) : new Date()}
                                            className="form-control form-control-sm custom-datepicker-input-width"
                                            dateFormat="dd/MM/yyyy"
                                            onChange={(d) => setFilterData({ ...filterData, endDate: moment(d).format('YYYY-MM-DD') })}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-sm-3">
                                <div className="row align-items-center">
                                    <div className="col-4"><label className="mb-0">User</label></div>
                                    <div className="col-8">
                                        <select
                                            className="form-select form-select-sm"
                                            value={selectedUserId}
                                            onChange={(e) => setSelectedUserId(e.target.value)}
                                        >
                                            <option value="">All Users</option>
                                            {userList.map((u) => (
                                                <option key={u.id} value={u.saas_user_id}>
                                                    {u.name} ({u.user_type})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                            <div className="col-sm-2">
                                <div className="d-flex gap-1">
                                    <button
                                        style={{ backgroundColor: '#69B128', color: 'white', paddingTop: '6px', paddingBottom: '7px' }}
                                        onClick={handleFilter}
                                        className="btn btn-sm px-3 fw-bold flex-fill"
                                    >
                                        Search
                                    </button>
                                    <button
                                        style={{ paddingTop: '6px', paddingBottom: '7px' }}
                                        onClick={() => {
                                            setFilterData({ startDate: date, endDate: date });
                                            setSelectedUserId('');
                                            fetchReport(date, date, '');
                                        }}
                                        className="btn btn-sm btn-outline-secondary fw-bold"
                                        title="Clear filters"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            {reportData.length > 0 && (
                <div className="row ms-1 me-1 mb-2 g-2">
                    {[
                        { label: 'Total Invoices', value: grandTotalInvoices, color: '#0d6efd', isNum: false },
                        { label: 'Grand Total (Total + Return)', value: num(grandTotalSales + grandTotalReturn), color: '#198754', isNum: true },
                        { label: 'Total Return', value: num(grandTotalReturn), color: '#dc3545', isNum: true },
                        { label: 'Total Sales', value: num(grandTotalPaid), color: '#69B128', isNum: true },
                    ].map((card, i) => (
                        <div className="col-sm-3" key={i}>
                            <div className="card text-white text-center py-2 px-3" style={{ backgroundColor: card.color }}>
                                <div style={{ fontSize: '0.78em', opacity: 0.9 }}>{card.label}</div>
                                <div className="fw-bold" style={{ fontSize: '1.1em' }}>{card.value}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Report Table */}
            <div className="me-1">
                <div className="custom-card p-2 report-table-wrapper g-doc-scroll">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">User Wise Report</h6>
                        <button onClick={handlePrint} className="btn btn-sm btn-primary">Print</button>
                    </div>
                    <div ref={reportRef} className="daily-sales-report-table">
                        {/* Print Header */}
                        <div className="daily-report">
                            <div className="d-flex justify-content-center">
                                <div className="text-center">
                                    <h5 className="report-org-name">{user?.organization?.name}</h5>
                                    <p className="report-org-address">{user?.organization?.address}</p>
                                    <p>Tel : {user?.organization?.mobile}</p>
                                </div>
                            </div>
                            <div className="d-flex justify-content-between mt-2">
                                <h6>User Wise Report{selectedUserLabel ? ` — ${selectedUserLabel}` : ''}</h6>
                                <h6>Dated: {moment(filterData.startDate).format('DD/MM/YYYY')} to {moment(filterData.endDate).format('DD/MM/YYYY')}</h6>
                            </div>
                        </div>

                        <table className="mt-2">
                            <thead>
                                <tr className="bg-light">
                                    <th style={{ width: 40 }}>#</th>
                                    <th>Invoice No</th>
                                    <th className="text-end">Total</th>
                                    <th className="text-end">Discount</th>
                                    <th className="text-end">Net Total</th>
                                    <th className="text-end">Paid Amount</th>
                                    <th>Return Invoice No</th>
                                    <th className="text-end">Return Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.length > 0 ? (
                                    reportData.map((userItem, uIdx) => {
                                        const invoices = userItem.invoices || [];
                                        const userPaid = invoices.reduce((s, inv) => s + parseFloat(inv.paid_amount || 0), 0);
                                        const userSales = invoices.reduce((s, inv) => s + parseFloat(inv.grand_total || 0), 0);
                                        const userReturn = invoices.reduce((s, inv) => s + parseFloat(inv.return_amount || 0), 0);
                                        const userNet = userPaid - userReturn;
                                        return (
                                            <React.Fragment key={uIdx}>
                                                {/* User header row */}
                                                <tr style={{ backgroundColor: '#e8f5e9', borderTop: '2px solid #69B128' }}>
                                                    <td colSpan="5" className="fw-bold py-1" style={{ fontSize: '16px' }}>
                                                        <span style={{ color: '#2e7d32', fontSize: '1.05em' }}>
                                                            {uIdx + 1}. {userItem.user_name}
                                                        </span>
                                                        <span
                                                            className="ms-2 badge"
                                                            style={{ backgroundColor: '#69B128', fontSize: '0.82em' }}
                                                        >
                                                            {userItem.user_type}
                                                        </span>
                                                        <span className="ms-3 text-muted fw-normal" style={{ fontSize: '0.92em' }}>
                                                            {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
                                                        </span>
                                                    </td>
                                                    <td colSpan="3" className="text-end fw-normal py-1" style={{ fontSize: '13px', color: '#555' }}>
                                                        Grand Total: <strong>{num(userSales + userReturn)}</strong>&nbsp;|&nbsp;Return: <strong style={{ color: '#dc3545' }}>{num(userReturn)}</strong>&nbsp;|&nbsp;Total Sales: <strong style={{ color: '#198754' }}>{num(userPaid)}</strong>
                                                    </td>
                                                </tr>
                                                {/* Invoice rows */}
                                                {invoices.map((inv, iIdx) => (
                                                    <tr key={iIdx} style={{ backgroundColor: iIdx % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                                        <td className="ps-3 text-muted" style={{ fontSize: '0.85em' }}>{iIdx + 1}</td>
                                                        <td>{inv.invoice_no}</td>
                                                        <td className="text-end">{num(inv.grand_total)}</td>
                                                        <td className="text-end text-warning">{inv.special_discount ? num(inv.special_discount) : '—'}</td>
                                                        <td className="text-end">{num(inv.Net_total)}</td>
                                                        <td className="text-end">{num(inv.paid_amount)}</td>
                                                        <td>
                                                            {inv.return_invoice_no
                                                                ? <span className="text-danger">{inv.invoice_no}</span>
                                                                : <span className="text-muted">—</span>
                                                            }
                                                        </td>
                                                        <td className="text-end text-danger">
                                                            {inv.return_amount ? num(inv.return_amount) : '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="text-center text-muted py-3">No data found</td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr style={{ backgroundColor: '#e8f5e9', fontWeight: 'bold', borderTop: '2px solid #69B128' }}>
                                    <td colSpan="2" className="text-end">Grand Total ({grandTotalInvoices} invoices)</td>
                                    <td className="text-end">{num(grandTotalSales)}</td>
                                    <td className="text-end text-warning">{num(grandTotalDiscount)}</td>
                                    <td className="text-end">{num(grandNetTotal)}</td>
                                    <td className="text-end">{num(grandTotalPaid)}</td>
                                    <td></td>
                                    <td className="text-end text-danger">{num(grandTotalReturn)}</td>
                                </tr>

                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
